import type { GetV1WorkspacesWorkspaceidFleetsFleetidDeploymentsQueryParamsSortEnumKey } from '@emma/api_client/types';
import type { InferRequestType, InferResponseType } from 'hono/client';
import { useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { type ApiClient, type ReadToastOptions, useFetch, useMutation } from '../api';

type GetDeployments =
  ApiClient['v1']['workspaces'][':workspaceId']['fleets'][':fleetId']['deployments']['$get'];
export type DeploymentListQuery = NonNullable<InferRequestType<GetDeployments>['query']>;
export type DeploymentListResponse = InferResponseType<GetDeployments, 200>;
export type DeploymentSort =
  GetV1WorkspacesWorkspaceidFleetsFleetidDeploymentsQueryParamsSortEnumKey;

type PostDeployment =
  ApiClient['v1']['workspaces'][':workspaceId']['fleets'][':fleetId']['deployments']['$post'];
export type CreateDeploymentRequest = InferRequestType<PostDeployment>['json'];
export type CreateDeploymentResponse = InferResponseType<PostDeployment, 201>;

export function useDeployments(
  workspaceId: number,
  fleetId: number,
  params: DeploymentListQuery = {},
  toastOptions?: ReadToastOptions
) {
  return useFetch<DeploymentListResponse>(
    `workspaces/${workspaceId}/fleets/${fleetId}/deployments?${JSON.stringify(params)}`,
    (client) =>
      client.v1.workspaces[':workspaceId'].fleets[':fleetId'].deployments.$get({
        param: { workspaceId: workspaceId.toString(), fleetId: fleetId.toString() },
        query: params,
      }),
    toastOptions,
    { keepPreviousData: true }
  );
}

export function useCreateDeployment(
  workspaceId: number,
  fleetId: number,
  actionOnErr: (error?: unknown) => void = () => {}
) {
  const { mutate } = useSWRConfig();
  const listCacheKey = `workspaces/${workspaceId}/fleets/${fleetId}/deployments`;
  const fleetCacheKey = `workspaces/${workspaceId}/fleets/${fleetId}`;

  const createDeploymentMutation = useMutation<CreateDeploymentResponse, CreateDeploymentRequest>(
    listCacheKey,
    (client, data) =>
      client.v1.workspaces[':workspaceId'].fleets[':fleetId'].deployments.$post({
        param: { workspaceId: workspaceId.toString(), fleetId: fleetId.toString() },
        json: data,
      }),
    actionOnErr,
    { suppressToast: true }
  );

  return useCallback(
    async (data: CreateDeploymentRequest) => {
      const result = await createDeploymentMutation(data);
      await mutate(
        (key: unknown) =>
          typeof key === 'string' && (key === listCacheKey || key.startsWith(`${listCacheKey}?`))
      );
      await mutate(fleetCacheKey);
      return result;
    },
    [createDeploymentMutation, mutate, listCacheKey, fleetCacheKey]
  );
}
